<template>
  <div class="sso-buttons">
    <p
      v-if="errorMessage"
      class="sso-error"
      role="alert"
      :data-sso-error="errorCode"
    >
      {{ errorMessage }}
      <span v-if="showSupport">
        Please contact
        <a href="mailto:support@bizminer.com">support@bizminer.com</a>.
      </span>
    </p>

    <div v-if="providers.length" class="sso-provider-list">
      <!-- Vendor sign-in buttons (Google Identity / Microsoft identity branding). Plain
           <button>s with explicit styles: the theme repaints md-button labels. -->
      <button
        v-if="providers.indexOf('google') !== -1"
        type="button"
        class="sso-btn sso-btn-google"
        :disabled="redirecting"
        @click="start('google')"
      >
        <svg class="sso-logo" width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
        </svg>
        <span class="sso-label">{{ labels.google }}</span>
      </button>
      <button
        v-if="providers.indexOf('microsoft') !== -1"
        type="button"
        class="sso-btn sso-btn-microsoft"
        :disabled="redirecting"
        @click="start('microsoft')"
      >
        <svg class="sso-logo" width="21" height="21" viewBox="0 0 21 21" aria-hidden="true" focusable="false">
          <rect x="1" y="1" width="9" height="9" fill="#f25022" />
          <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
          <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
          <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
        </svg>
        <span class="sso-label">{{ labels.microsoft }}</span>
      </button>
      <p class="sso-divider"><span>or</span></p>
    </div>
  </div>
</template>

<script>
import axios from "axios";

// Same localStorage key every other auth call in the app uses for the client session_id.
const SESSION_KEY = "vue-authenticate.vueauth_access_token";
// Shape the API accepts (SsoAccountService::SESSION_ID_PATTERN).
const SESSION_ID_PATTERN = /^[A-Za-z0-9]{16,64}$/;

const KNOWN_PROVIDERS = ["google", "microsoft"];

// sso_error codes the API can send back to /login (brief section 5). The raw code is only
// ever exposed as a data attribute; users see the copy.
const ERROR_COPY = {
  sso_state_mismatch: { text: "Sign-in failed, please try again." },
  sso_provider_error: { text: "Sign-in failed, please try again." },
  sso_email_unverified: {
    text:
      "We couldn't confirm the email address on that account. Verify it with your provider, or log in with your email and password.",
  },
  sso_account_disabled: { text: "This account is disabled.", support: true },
  sso_link_conflict: {
    text: "That sign-in is already linked to a different Bizminer account.",
    support: true,
  },
  sso_ticket_invalid: { text: "Sign-in expired, please try again." },
};

export default {
  name: "sso-buttons",
  data: () => ({
    providers: [],
    redirecting: false,
    errorCode: null,
    labels: {
      google: "Continue with Google",
      microsoft: "Continue with Microsoft",
    },
  }),
  computed: {
    errorMessage() {
      if (!this.errorCode) return null;
      const entry = ERROR_COPY[this.errorCode];
      return entry ? entry.text : "Sign-in failed, please try again.";
    },
    showSupport() {
      const entry = this.errorCode ? ERROR_COPY[this.errorCode] : null;
      return !!(entry && entry.support);
    },
  },
  created() {
    // Captured once: the login page's authenticateUser() call re-pushes /login without the
    // query string, which would otherwise wipe the message a moment after it appears.
    const code = this.$route && this.$route.query ? this.$route.query.sso_error : null;
    if (typeof code === "string" && code !== "") {
      this.errorCode = Object.prototype.hasOwnProperty.call(ERROR_COPY, code) ? code : "unknown";
    }
    this.loadProviders();
  },
  methods: {
    async loadProviders() {
      try {
        const res = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/session/sso/enabled`
        );
        const data = res && res.data ? res.data : {};
        const listed = Array.isArray(data.providers) ? data.providers : [];
        this.providers = data.enabled === true
          ? KNOWN_PROVIDERS.filter((p) => listed.indexOf(p) !== -1)
          : [];
      } catch (e) {
        // Flag endpoint unreachable / throttled: no SSO buttons, password login unaffected.
        this.providers = [];
      }
    },

    // Same session_id source as Login.vue's checkSessionId(): the stored token, or a new one
    // from the store's getRandomString action (auth.js), saved under the same key.
    async ensureSessionId() {
      let sessionId = localStorage.getItem(SESSION_KEY);
      if (!sessionId || !SESSION_ID_PATTERN.test(sessionId)) {
        sessionId = await this.$store.dispatch("getRandomString", 20);
        localStorage.setItem(SESSION_KEY, sessionId);
      }
      return sessionId;
    },

    async start(provider) {
      if (this.redirecting || KNOWN_PROVIDERS.indexOf(provider) === -1) return;
      this.redirecting = true;
      try {
        const sessionId = await this.ensureSessionId();
        window.location.href =
          `${process.env.VUE_APP_API_BASE_URL}/session/sso/${provider}/redirect` +
          `?session_id=${encodeURIComponent(sessionId)}`;
      } catch (e) {
        this.redirecting = false;
      }
    },
  },
};
</script>

<style scoped>
.sso-buttons {
  margin: 0.25em 0 0.5em;
}
.sso-provider-list {
  display: flex;
  flex-direction: column;
}
.sso-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin: 0 0 10px;
  padding: 0 12px;
  background: #ffffff !important;
  cursor: pointer;
  text-transform: none !important;
  letter-spacing: normal;
  line-height: 1;
  box-shadow: none;
  transition: background-color 0.15s, box-shadow 0.15s;
}
.sso-btn:focus {
  outline: none;
}
.sso-btn:focus-visible {
  outline: 2px solid #1a73e8;
  outline-offset: 2px;
}
.sso-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
.sso-logo {
  flex: 0 0 auto;
  margin-right: 12px;
}
/* Google Identity branding: white, 1px #dadce0, #3c4043 medium label. */
.sso-btn-google {
  height: 40px;
  border: 1px solid #dadce0;
  border-radius: 4px;
  color: #3c4043 !important;
  font-family: Roboto, Arial, sans-serif;
  font-size: 14px;
  font-weight: 500;
}
.sso-btn-google:hover:not(:disabled) {
  background: #f8f9fa !important;
  box-shadow: 0 1px 2px rgba(60, 64, 67, 0.3), 0 1px 3px 1px rgba(60, 64, 67, 0.15);
}
/* Microsoft identity branding: white, 1px #8c8c8c, #5e5e5e semibold label. */
.sso-btn-microsoft {
  height: 41px;
  border: 1px solid #8c8c8c;
  border-radius: 0;
  color: #5e5e5e !important;
  font-family: "Segoe UI", Arial, sans-serif;
  font-size: 15px;
  font-weight: 600;
}
.sso-btn-microsoft:hover:not(:disabled) {
  background: #f3f3f3 !important;
}
.sso-label {
  color: inherit;
}
.sso-divider {
  display: flex;
  align-items: center;
  margin: 4px 0 12px;
  color: #6b6b6b;
  font-size: 13px;
}
.sso-divider::before,
.sso-divider::after {
  content: "";
  flex: 1 1 auto;
  border-top: 1px solid #dadce0;
}
.sso-divider span {
  padding: 0 10px;
}
.sso-error {
  color: #d32f2f;
  margin: 0.75em 0;
  text-align: center;
}
</style>
